import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
    Table,
    InputNumber,
} from 'antd';
import dayjs from 'dayjs';
import { PlusOutlined } from '@ant-design/icons';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { useNavigate } from 'react-router-dom';
import { WoodType, Ledger, Wood } from '../../../../types/CreateProductType';

interface WoodModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void;
    initialValues?: any;
    isEditMode?: boolean;
    woodTypesData?: WoodType[];
    ledgerData?: Ledger[];
    woodData?: Wood[];
}

interface GoodsRow {
    key: string;
    description: string;
    wood_type: string;
    hsn: string;
    cft: number;
    rate: number;
    amount: number;
}
const truncateTwoDecimals = (num: number): number => {
    return Math.floor(num * 100) / 100;
};
const AddPO: React.FC<WoodModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
    woodTypesData = [],
    ledgerData = [],
    woodData = [],
}) => {
    const [form] = Form.useForm();
    const [goodsData, setGoodsData] = useState<GoodsRow[]>([]);
    const [isModalClose, setIsModalClose] = useState(true);

    const [ledgers, setLedgers] = useState<Ledger[]>([]);

    const [dropdownOptions, setDropdownOptions] = useState<{ label: string; value: number }[]>([]);
    const [piList, setPiList] = useState<any[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        if (visible) {
            const fetchPIs = async () => {
                try {
                    const data = await apiRequest<any[]>({
                        method: 'get',
                        url: '/get-performa-invoices',
                    });
                    setPiList(data || []);
                } catch (error) {
                    console.error('Failed to fetch performa invoices:', error);
                }
            };
            fetchPIs();
        }
    }, [visible]);

    useEffect(() => {
        if (visible && initialValues) {
            form.setFieldsValue({
                ledger_id: initialValues.ledger_id || initialValues.pbill_ledger_id,
                bill_no: initialValues.bill_no || initialValues.pbill_bill_no,
                challan_no: initialValues.challan_no || initialValues.pbill_challan_no,
                pbill_po_no: initialValues.pbill_po_no || '',
                bill_date: initialValues.bill_date
                    ? (dayjs.isDayjs(initialValues.bill_date)
                        ? initialValues.bill_date
                        : dayjs(initialValues.bill_date, ["DD-MM-YYYY", "YYYY-MM-DD"]))
                    : null,
                motor_vehicle_no: (initialValues.motor_vehicle_no || initialValues.pbill_motor_vehicle_no || '') ? String(initialValues.motor_vehicle_no || initialValues.pbill_motor_vehicle_no).toUpperCase() : '',
                freight_outward: initialValues.freight_outward || initialValues.pbill_fright_amount,
            });

            if (Array.isArray(initialValues.po_details)) {
                const goods = initialValues.po_details.map((item: any) => ({
                    key: item.pbg_id ? String(item.pbg_id) : Date.now().toString() + Math.random(),
                    description: item.pbg_description ? Number(item.pbg_description) : undefined,
                    wood_type: item.pbg_wood_type ? Number(item.pbg_wood_type) : undefined,
                    hsn: item.pbg_hsn || '',
                    cft: Number(item.pbg_cft || item.pbg_qty || 0),
                    rate: Number(item.pbg_rate || 0),
                    amount: Number(item.pbg_amount || (item.pbg_cft || 0) * (item.pbg_rate || 0)),
                }));
                setGoodsData(goods);
            } else {
                setGoodsData([]);
            }
        } else if (visible) {
            form.resetFields();
            setGoodsData([]);
        }
    }, [visible, initialValues]);

    const addGoodsRow = () => {
        setGoodsData(prev => [
            ...prev,
            {
                key: Date.now().toString(),
                description: '',
                wood_type: '',
                hsn: '',
                cft: 0,
                rate: 0,
                amount: 0,
            },
        ]);
    };

    const handleGoodsChange = (value: any, key: string, field: keyof GoodsRow) => {
        const updated = goodsData.map(item => {
            if (item.key === key) {
                const newItem = { ...item, [field]: value };
                newItem.amount = newItem.cft * newItem.rate;
                return newItem;
            }
            return item;
        });
        setGoodsData(updated);
    };

    const goodsColumns = [
        {
            title: 'Sr',
            dataIndex: 'sr',
            render: (_: any, __: any, index: number) => index + 1,
        },
        {
            title: 'Description of Goods',
            dataIndex: 'description',
            render: (_: any, record: GoodsRow) => (
                <div style={{ marginBottom: 16 }}>
                    <Select
                        showSearch
                        placeholder="Select wood"
                        value={record.description || undefined}
                        onChange={value => handleGoodsChange(value, record.key, "description")}
                        options={woodData.map(w => ({
                            label: w.wood_name,
                            value: w.wood_id,
                        }))}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: '100%', marginBottom: 8 }} // optional spacing between selects
                    />
                    <Select
                        showSearch
                        value={record.wood_type || undefined}
                        options={woodTypesData.map(w => ({
                            label: w.wood_name,
                            value: w.wood_id,
                        }))}
                        onChange={value => handleGoodsChange(value, record.key, "wood_type")}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        placeholder="Select Wood Type"
                        style={{ width: '100%' }}
                    />
                </div>
            ),
        },
        {
            title: 'CFT',
            dataIndex: 'cft',
            render: (_: any, record: GoodsRow) => (
                <InputNumber
                    min={0}
                    controls={false}
                    value={record.cft}
                    onChange={value => handleGoodsChange(value || 0, record.key, 'cft')}
                />
            ),
        },
        {
            title: "Rate",
            dataIndex: "rate",
            render: (_: any, record: GoodsRow) => (
                <InputNumber
                    min={0}
                    step={0.0001}
                    precision={4}
                    value={record.rate}
                    parser={(value = "") => {
                        const clean = value.replace(/[^\d.]/g, "");
                        const parts = clean.split(".");
                        return (parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : clean) as any;
                    }}
                    onKeyDown={(e) => {
                        if (
                            e.key === " " ||
                            e.key === "-" ||
                            e.key === "+"
                        ) {
                            e.preventDefault();
                        }
                    }}
                    onChange={(value) =>
                        handleGoodsChange(value || 0, record.key, "rate")
                    }
                />
            ),
        },

        {
            title: 'Amount',
            dataIndex: 'amount',
            render: (_: any, record: GoodsRow) => <span>{record.amount.toFixed(4)}</span>,
        },
        {
            title: 'Action',
            dataIndex: 'action',
            render: (_: any, record: GoodsRow) => (
                <Button
                    type="link"
                    danger
                    onClick={() => handleRemoveGoodsRow(record.key)}
                >
                    Remove
                </Button>
            ),
        },
    ];
    const handleRemoveGoodsRow = (key: string) => {
        const updatedData = goodsData.filter(item => item.key !== key);
        setGoodsData(updatedData);
    };

    const totalAmount = goodsData.reduce((sum, row) => sum + row.amount, 0);
    const freightOutwardWatch = Form.useWatch('freight_outward', form);
    const freightOutward = Number(freightOutwardWatch ?? form.getFieldValue('freight_outward') ?? 0);
    const adjustedTotalAmount = Number(totalAmount) + Number(freightOutward);
    // Calculate taxes
    const cgst = truncateTwoDecimals(adjustedTotalAmount * 0.09);
    const sgst = truncateTwoDecimals(adjustedTotalAmount * 0.09);
    const grandTotal = Number(adjustedTotalAmount) + cgst + sgst;

    // Round off to nearest integer
    const roundedGrandTotal = Math.round(Number(grandTotal));

    // console.log(roundedGrandTotal);

    const handleSubmit = async (values: any) => {
        const payload = {
            pbill_id: isEditMode && initialValues ? initialValues.pbill_id : undefined,
            ...values,
            bill_type: 'timber',
            bill_date: values.bill_date ? dayjs(values.bill_date).format('YYYY-MM-DD') : null,
            goods: goodsData.map((g: any) => ({
                ...g,
                pbg_issued_cft: g.pbg_issued_cft || 0,
                issued_cft: g.issued_cft || g.pbg_issued_cft || 0,
            })),
            total_amount: totalAmount,
            cgst,
            sgst,
            freight_outward: freightOutward,
            grand_total: roundedGrandTotal,
        };

        try {
            const url = isEditMode ? 'update-hpo' : 'create-new-hpo';
            const res = await apiRequest({
                method: 'post',
                url,
                data: payload,
            });

            showSuccess(isEditMode ? 'Bill updated successfully!' : 'Bill created successfully!');
            form.resetFields();
            setGoodsData([]);
            onCreate(res);

            if (!isEditMode && res?.pbill_id) {
                navigate(`/party-napi/${res?.pbill_id}`);
            }

            if (isModalClose || isEditMode) {
                onCancel();
            }

        } catch (err) {
            showError('Failed to submit');
        }
    };

    const fetchLedger = async (mobile?: string) => {
        try {
            let url = `h-ledger-data`;
            if (mobile) url += `?mobile=${encodeURIComponent(mobile)}`;

            const response = await apiRequest<{ ledgers: Ledger[] }>({
                method: 'get',
                url,
            });
            console.log("ledger", response);
            const fetched = response.ledgers || [];
            setLedgers(fetched);

            // Map to dropdown options
            const options = fetched.map((ledger) => ({
                label: `${ledger.ledger_first_name} ${ledger.ledger_last_name || ''}`,
                value: ledger.ledger_id,
            }));

            setDropdownOptions(options);

            // Auto-select if only one match
            if (options.length === 1) {
                form.setFieldsValue({
                    handover_full_name: options[0].value,
                });
            } else {
                form.setFieldsValue({ handover_full_name: undefined });
            }

        } catch (error) {
            showError('Failed to fetch Ledger');
            console.error(error);
        }
    };
    return (
        <Modal
            title={isEditMode ? 'Edit Wood PO' : 'Add New Wood PO'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="95%"
            destroyOnHidden
            centered
        >
            <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
                <Row gutter={16}>

                    <Col span={12}>
                        <Form.Item name="ledger_id" label="Company Name" rules={[{ required: true }]}>
                            <Select
                                options={ledgerData.map(l => ({
                                    label: `${l.ledger_company_name} (${l.ledger_gstin}) `,
                                    value: l.ledger_id,
                                }))}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                showSearch
                                placeholder="Select Company"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="bill_no" label="Bill No" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="challan_no" label="Challan No">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="bill_date" label="Bill Date">
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            name="motor_vehicle_no"
                            label="Motor Vehicle No"
                            normalize={(value) => (value ? value.toUpperCase() : value)}
                        >
                            <Input style={{ textTransform: 'uppercase' }} placeholder="Enter vehicle no" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            name="freight_outward"
                            label="Freight Outward Amount"
                        >
                            <InputNumber
                                min={0}
                                precision={4}
                                step={0.0001}
                                style={{ width: '100%' }}
                                parser={(value = '') => {
                                    const clean = value.replace(/[^\d.]/g, '');
                                    const parts = clean.split('.');
                                    return (parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : clean) as any;
                                }}
                                onKeyDown={(e) => {
                                    if (
                                        e.key === ' ' ||
                                        e.key === '-' ||
                                        e.key === '+'
                                    ) {
                                        e.preventDefault();
                                    }
                                }}
                            />

                        </Form.Item>
                    </Col>


                    <Col span={6}>
                        <Form.Item name="pbill_po_no" label="PO No">
                            <Select
                                showSearch
                                placeholder="Select PO No"
                                options={[
                                    { label: "SVC 0", value: "0" },
                                    ...piList.map((pi: any) => ({
                                        label: pi.pi_order_no,
                                        value: pi.pi_order_no,
                                    }))
                                ]}
                                filterOption={(input, option) =>
                                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Goods Table */}
                <Table
                    dataSource={goodsData}
                    columns={goodsColumns}
                    pagination={false}
                    footer={() => (
                        <Button type="dashed" icon={<PlusOutlined />} onClick={addGoodsRow}>
                            Add Row
                        </Button>
                    )}
                />

                {/* Totals */}
                <Row gutter={16} style={{ marginTop: 20 }}>
                    <Col span={6}>
                        <strong>Total Amount:</strong> ₹{Number(adjustedTotalAmount.toFixed(4))}
                    </Col>
                    <Col span={6}>
                        <strong>CGST (9%):</strong> ₹{cgst}
                    </Col>
                    <Col span={6}>
                        <strong>SGST (9%):</strong> ₹{sgst}
                    </Col>
                    <Col span={6}>
                        <strong>Grand Total:</strong> ₹{roundedGrandTotal.toFixed(4)}
                    </Col>
                </Row>

                {/* Buttons */}
                <Form.Item style={{ textAlign: 'right', marginTop: 30 }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && (
                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={() => setIsModalClose(false)}
                            >
                                Submit & Create New
                            </Button>
                        )}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddPO;
